import React, { useEffect, useState, useCallback, useRef } from 'react';
import Button from '../../../components/common/Button';
import Icon from '../../../components/common/Icon';
import Input from '../../../components/common/Input';
import ExportButton from '../../../components/common/ExportButton';
import Pagination from '../../../components/common/Pagination';
import Modal from '../../../components/common/Modal';

import {
 exportPlayersApi,
 getPlayersApi,
 deletePlayerApi,
 updatePlayerStatusApi,
} from '../../../api/player.api';

import { getClubsApi } from '../../../api/club.api';

import {
  AGE_GROUP_LABELS,
  GENDER_LABELS,
  PLAYER_STATUS_LABELS,
} from '../../../utils/constants';

import PlayerForm from '../../../components/forms/PlayerForm';
import PlayersTable from '../../../components/tables/PlayersTable';

const EMPTY_FILTERS = {
  clubId: '',
  ageGroup: '',
  gender: '',
  status: '',
  search: '',
  missingNationalCode: '',
};

export default function PlayersManagement() {
  const [players, setPlayers] = useState([]);
  const [clubs, setClubs] = useState([]);

  const [filters, setFilters] = useState(EMPTY_FILTERS);

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  });

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [snackbar, setSnackbar] = useState({
    message: '',
    type: 'success',
  });

  const [loading, setLoading] = useState(false);

  const requestIdRef = useRef(0);

  /* =========================================================
     SNACKBAR
  ========================================================= */

  const showSnackbar = useCallback((message, type = 'success') => {
    setSnackbar({
      message,
      type,
    });

    setTimeout(() => {
      setSnackbar({
        message: '',
        type: 'success',
      });
    }, 3500);
  }, []);

  /* =========================================================
     LOAD CLUBS
  ========================================================= */

  const loadClubs = useCallback(async () => {
    try {
      const { data } = await getClubsApi({
        limit: 100,
      });

      setClubs(data?.data || []);
    } catch (err) {
      showSnackbar(
        err?.response?.data?.message ||
          'دریافت لیست باشگاه‌ها با خطا مواجه شد',
        'error'
      );
    }
  }, [showSnackbar]);

  /* =========================================================
     LOAD PLAYERS
  ========================================================= */

  const loadPlayers = useCallback(async () => {
    const currentRequestId = ++requestIdRef.current;

    setLoading(true);

    try {
      const cleanFilters = Object.fromEntries(
        Object.entries(filters).filter(
          ([, value]) => value !== ''
        )
      );

      const { data } = await getPlayersApi({
        ...cleanFilters,
        page: pagination.page,
        limit: pagination.limit,
      });

      if (currentRequestId !== requestIdRef.current) {
        return;
      }

      const playerData = data?.data || [];
      const meta = data?.meta || {};

      setPlayers(playerData);

      const total = Number(meta.total || 0);
      const totalPages = Math.max(
        Number(meta.totalPages || 1),
        1
      );

      setPagination((prev) => ({
        ...prev,
        total,
        totalPages,
        page:
          prev.page > totalPages
            ? totalPages
            : prev.page,
      }));
    } catch (err) {
      if (currentRequestId !== requestIdRef.current) {
        return;
      }

      setPlayers([]);

      showSnackbar(
        err?.response?.data?.message ||
          'خطا در دریافت اطلاعات بازیکنان',
        'error'
      );
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [
    filters,
    pagination.page,
    pagination.limit,
    showSnackbar,
  ]);

  /* =========================================================
     INITIAL LOAD
  ========================================================= */

  useEffect(() => {
    loadClubs();
  }, [loadClubs]);

  /* =========================================================
     SEARCH DEBOUNCE
  ========================================================= */

  useEffect(() => {
    const timer = setTimeout(() => {
      loadPlayers();
    }, filters.search ? 450 : 0);

    return () => clearTimeout(timer);
  }, [
    filters,
    pagination.page,
    pagination.limit,
    loadPlayers,
  ]);

  /* =========================================================
     FILTER
  ========================================================= */

  const updateFilter = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));

    setPagination((prev) => ({
      ...prev,
      page: 1,
    }));
  };

  const resetFilters = () => {
    setFilters(EMPTY_FILTERS);

    setPagination((prev) => ({
      ...prev,
      page: 1,
    }));
  };

  const hasActiveFilters = Object.values(filters).some(
    (value) => value !== ''
  );

  /* =========================================================
     PAGINATION
  ========================================================= */

  const handlePageChange = (newPage) => {
    if (
      newPage < 1 ||
      newPage > pagination.totalPages ||
      newPage === pagination.page
    ) {
      return;
    }

    setPagination((prev) => ({
      ...prev,
      page: newPage,
    }));
  };

  /* =========================================================
     FORM
  ========================================================= */

  const openCreateForm = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEditForm = (player) => {
    setEditing(player);
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditing(null);
  };

  const handlePlayerSaved = async () => {
    await loadPlayers();

    showSnackbar(
      editing
        ? 'اطلاعات بازیکن با موفقیت ویرایش شد'
        : 'بازیکن با موفقیت ثبت شد',
      'success'
    );
  };

  /* =========================================================
     TOGGLE STATUS (فعال/غیرفعال)
  ========================================================= */

  const togglePlayerStatus = useCallback(async (playerId, currentStatus) => {
    // اگر بازیکن در انتظار تایید بود، این عملیات همان "تایید بازیکن" است
    const newStatus = currentStatus === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
    try {
      await updatePlayerStatusApi(
        playerId,
        newStatus
      );
      showSnackbar(
        newStatus === 'ACTIVE' ? 'بازیکن تایید و فعال شد' : 'وضعیت بازیکن به غیرفعال تغییر کرد',
        'success'
      );
      loadPlayers();
    } catch (err) {
      showSnackbar(
        err?.response?.data?.message || 'خطا در تغییر وضعیت',
        'error'
      );
    }
  }, [loadPlayers, showSnackbar]);

  const rejectPlayer = useCallback(async (playerId) => {
    try {
      await updatePlayerStatusApi(playerId, 'REJECTED');
      showSnackbar('بازیکن رد شد', 'success');
      loadPlayers();
    } catch (err) {
      showSnackbar(
        err?.response?.data?.message || 'خطا در رد بازیکن',
        'error'
      );
    }
  }, [loadPlayers, showSnackbar]);

  /* =========================================================
     DELETE (غیرفعال‌سازی کامل)
  ========================================================= */

  const handleDelete = async () => {
    if (!deleteTarget) {
      return;
    }

    try {
      await deletePlayerApi(deleteTarget);

      setDeleteTarget(null);

      showSnackbar(
        'بازیکن با موفقیت غیرفعال شد',
        'success'
      );

      await loadPlayers();
    } catch (err) {
      showSnackbar(
        err?.response?.data?.message ||
          'غیرفعال‌ سازی بازیکن انجام نشد',
        'error'
      );
    }
  };

  /* =========================================================
     STATISTICS
  ========================================================= */

  const activeCount = players.filter(
    (player) => player.status === 'ACTIVE'
  ).length;

  const inactiveCount = players.filter(
    (player) => player.status === 'INACTIVE'
  ).length;

  /* =========================================================
     RENDER
  ========================================================= */

  return (
    <div className="space-y-6">

      {/* ================= HEADER ================= */}

      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">

        <div>
          <div className="flex items-center gap-3">

            <div className="grid h-12 w-12 place-items-center rounded-2xl bg-gradient-to-br from-emerald-50 to-emerald-100 text-emerald-600 shadow-sm">
              <Icon
                name="users"
                className="h-6 w-6"
              />
            </div>

            <div>
              <h1 className="text-2xl font-black text-text-dark">
                مدیریت بازیکنان
              </h1>

              <p className="mt-1 text-sm text-slate-500">
                مدیریت و بررسی بازیکنان تمامی باشگاه‌ها
              </p>
            </div>

          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <ExportButton
            fileName="players.xlsx"
            onExport={() =>
              exportPlayersApi(
                Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== ''))
              )
            }
          />

          <Button
            variant="primary"
            onClick={openCreateForm}
            className="h-11 rounded-xl px-5 shadow-sm"
          >
            <Icon
              name="plus"
              className="ml-2 h-4 w-4"
            />

            افزودن بازیکن
          </Button>
        </div>

      </div>

      {/* ================= STATISTICS ================= */}

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">

        <StatCard
          title="کل بازیکنان"
          value={pagination.total}
          icon="users"
          className="bg-emerald-50 text-emerald-600"
        />

        <StatCard
          title="این صفحه"
          value={players.length}
          icon="user"
          className="bg-blue-50 text-blue-600"
        />

        <StatCard
          title="فعال"
          value={activeCount}
          icon="check-circle"
          className="bg-green-50 text-green-600"
        />

        <StatCard
          title="غیرفعال"
          value={inactiveCount}
          icon="archive"
          className="bg-slate-100 text-slate-600"
        />

      </div>

      {/* ================= FILTERS ================= */}

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">

        <div className="border-b border-slate-100 px-5 py-4">

          <div className="flex items-center justify-between gap-3">

            <div className="flex items-center gap-2">

              <div className="grid h-8 w-8 place-items-center rounded-lg bg-emerald-50 text-emerald-600">
                <Icon
                  name="filter"
                  className="h-4 w-4"
                />
              </div>

              <h2 className="text-sm font-black text-text-dark">
                فیلتر و جستجوی بازیکنان
              </h2>

            </div>

            {hasActiveFilters && (
              <button
                type="button"
                onClick={resetFilters}
                className="flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-bold text-slate-400 transition hover:bg-red-50 hover:text-red-500"
              >
                <Icon
                  name="close"
                  className="h-3.5 w-3.5"
                />

                پاک کردن فیلترها
              </button>
            )}

          </div>

        </div>

        <div className="grid gap-3 p-5 sm:grid-cols-2 lg:grid-cols-5">

          {/* CLUB */}

          <FilterSelect
            value={filters.clubId}
            onChange={(value) =>
              updateFilter('clubId', value)
            }
            options={clubs}
            placeholder="همه باشگاه‌ها"
            optionLabel="name"
          />

          {/* AGE */}

          <FilterSelect
            value={filters.ageGroup}
            onChange={(value) =>
              updateFilter('ageGroup', value)
            }
            options={Object.entries(
              AGE_GROUP_LABELS
            ).map(([key, value]) => ({
              id: key,
              name: value,
            }))}
            placeholder="همه گروه‌های سنی"
          />

          {/* GENDER */}

          <FilterSelect
            value={filters.gender}
            onChange={(value) =>
              updateFilter('gender', value)
            }
            options={Object.entries(
              GENDER_LABELS
            ).map(([key, value]) => ({
              id: key,
              name: value,
            }))}
            placeholder="همه جنسیت‌ ها"
          />

          {/* STATUS */}

          <FilterSelect
            value={filters.status}
            onChange={(value) =>
              updateFilter('status', value)
            }
            options={Object.entries(
              PLAYER_STATUS_LABELS
            ).map(([key, value]) => ({
              id: key,
              name: value,
            }))}
            placeholder="همه وضعیت‌ ها"
          />

          {/* SEARCH */}

          <div className="relative">

            <Input
              placeholder="نام، نام خانوادگی یا کد ملی..."
              value={filters.search}
              onChange={(e) =>
                updateFilter(
                  'search',
                  e.target.value
                )
              }
              className="h-11! pr-10"
            />

            <Icon
              name="search"
              className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
            />

          </div>

          {/* بازیکنان قدیمی که هنوز کد ملی ندارند */}

          <button
            type="button"
            onClick={() =>
              updateFilter(
                'missingNationalCode',
                filters.missingNationalCode ? '' : 'true'
              )
            }
            className={`h-11 rounded-xl border px-3 text-xs font-bold transition ${
              filters.missingNationalCode
                ? 'border-red-300 bg-red-50 text-red-600'
                : 'border-slate-200 bg-slate-50 text-slate-600 hover:border-slate-300'
            }`}
            title="نمایش بازیکنانی که هنوز کد ملی برایشان ثبت نشده"
          >
            {filters.missingNationalCode ? '✓ ' : ''}فقط بدون کد ملی
          </button>

        </div>
      </div>

      {/* ================= SNACKBAR ================= */}

      {snackbar.message && (
        <div
          className={`
            flex items-center justify-between
            rounded-xl border px-4 py-3
            text-sm font-medium
            ${
              snackbar.type === 'error'
                ? 'border-red-100 bg-red-50 text-red-700'
                : 'border-emerald-100 bg-emerald-50 text-emerald-700'
            }
          `}
        >

          <div className="flex items-center gap-2">

            <Icon
              name={
                snackbar.type === 'error'
                  ? 'alert-circle'
                  : 'check-circle'
              }
              className="h-4 w-4"
            />

            {snackbar.message}

          </div>

          <button
            type="button"
            onClick={() =>
              setSnackbar({
                message: '',
                type: 'success',
              })
            }
            className="rounded-lg p-1 transition hover:bg-black/5"
          >
            ✕
          </button>

        </div>
      )}

      {/* ================= TABLE ================= */}

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">

        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">

          <div>
            <h2 className="font-black text-text-dark">
              فهرست بازیکنان
            </h2>

            <p className="mt-1 text-xs text-slate-400">
              {pagination.total.toLocaleString('fa-IR')}
              {' '}
              بازیکن ثبت شده
            </p>
          </div>

          {loading && (
            <div className="flex items-center gap-2 text-xs font-bold text-emerald-600">

              <span className="h-4 w-4 animate-spin rounded-full border-2 border-emerald-500 border-t-transparent" />

              در حال بروزرسانی...

            </div>
          )}

        </div>

        <div className="p-4">

          {loading ? (
            <PlayersSkeleton />
          ) : players.length === 0 ? (
            <EmptyPlayers
              onAdd={openCreateForm}
              hasFilters={hasActiveFilters}
              onReset={resetFilters}
            />
          ) : (
            <>

              <PlayersTable
                players={players}
                showClub
                onEdit={openEditForm}
                onDelete={(id) =>
                  setDeleteTarget(id)
                }
                onToggleStatus={togglePlayerStatus} // ← اضافه شد
                onReject={rejectPlayer}
                onRefresh={loadPlayers}
              />

              <div className="mt-5 border-t border-slate-100 pt-5">

                <Pagination
                  page={pagination.page}
                  totalPages={pagination.totalPages}
                  onPageChange={handlePageChange}
                />

              </div>

            </>
          )}

        </div>
      </div>

      {/* ================= PLAYER FORM ================= */}

      <PlayerForm
        open={formOpen}
        onClose={closeForm}
        onSaved={handlePlayerSaved}
        player={editing}
        clubs={clubs}
        isAdmin
      />

      {/* ================= DELETE MODAL ================= */}

      <Modal
        open={Boolean(deleteTarget)}
        onClose={() =>
          setDeleteTarget(null)
        }
        title="غیرفعال‌ سازی بازیکن"
      >

        <div className="space-y-5">

          <div className="flex items-start gap-3 rounded-xl bg-red-50 p-4">

            <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-red-100 text-red-600">

              <Icon
                name="alert-circle"
                className="h-5 w-5"
              />

            </div>

            <div>

              <p className="font-bold text-red-700">
                آیا از غیرفعال‌ سازی این بازیکن مطمئن هستید؟
              </p>

              <p className="mt-1 text-xs leading-6 text-red-500">
                اطلاعات بازیکن حذف دائمی نمی‌ شود و وضعیت او به غیرفعال تغییر خواهد کرد.
              </p>

            </div>

          </div>

          <div className="flex justify-end gap-2">

            <Button
              variant="ghost"
              onClick={() =>
                setDeleteTarget(null)
              }
            >
              انصراف
            </Button>

            <Button
              variant="primary"
              onClick={handleDelete}
            >
              غیرفعال‌ سازی
            </Button>

          </div>

        </div>

      </Modal>

    </div>
  );
}

/* =========================================================
   FILTER SELECT
========================================================= */

function FilterSelect({
  value,
  onChange,
  options = [],
  placeholder,
}) {
  return (
    <select
      value={value}
      onChange={(e) =>
        onChange(e.target.value)
      }
      className="
        h-11 w-full rounded-xl
        border border-slate-200
        bg-white px-3
        text-sm text-slate-700
        outline-none
        transition-all
        hover:border-slate-300
        focus:border-emerald-400
        focus:ring-2
        focus:ring-emerald-500/20
      "
    >
      <option value="">
        {placeholder}
      </option>

      {options.map((option) => (
        <option
          key={option.id}
          value={option.id}
        >
          {option.name}
        </option>
      ))}
    </select>
  );
}

/* =========================================================
   STAT CARD
========================================================= */

function StatCard({
  title,
  value,
  icon,
  className = '',
}) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md">

      <div className="flex items-center justify-between">

        <div>

          <p className="text-xs font-bold text-slate-400">
            {title}
          </p>

          <p className="mt-2 text-2xl font-black text-text-dark">
            {Number(value || 0).toLocaleString('fa-IR')}
          </p>

        </div>

        <div
          className={`
            grid h-11 w-11
            place-items-center
            rounded-xl
            ${className}
          `}
        >
          <Icon
            name={icon}
            className="h-5 w-5"
          />
        </div>

      </div>

    </div>
  );
}

/* =========================================================
   SKELETON
========================================================= */

function PlayersSkeleton() {
  return (
    <div className="space-y-3">

      {Array.from({ length: 6 }).map(
        (_, index) => (
          <div
            key={index}
            className="animate-pulse rounded-xl bg-slate-50 p-4"
          >

            <div className="flex items-center gap-4">

              <div className="h-12 w-12 rounded-full bg-slate-200" />

              <div className="flex-1 space-y-2">

                <div className="h-3 w-40 rounded bg-slate-200" />

                <div className="h-3 w-24 rounded bg-slate-100" />

              </div>

              <div className="hidden h-8 w-24 rounded bg-slate-200 md:block" />

            </div>

          </div>
        )
      )}

    </div>
  );
}

/* =========================================================
   EMPTY
========================================================= */

function EmptyPlayers({
  onAdd,
  hasFilters,
  onReset,
}) {
  return (
    <div className="py-16 text-center">

      <div className="mx-auto grid h-16 w-16 place-items-center rounded-2xl bg-slate-100 text-slate-400">

        <Icon
          name="users"
          className="h-7 w-7"
        />

      </div>

      <h3 className="mt-4 font-black text-text-dark">
        بازیکنی پیدا نشد
      </h3>

      <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-slate-400">
        {hasFilters
          ? 'با تغییر فیلترها دوباره جستجو کنید.'
          : 'هنوز بازیکنی در سیستم ثبت نشده است.'}
      </p>

      <div className="mt-5 flex justify-center gap-2">

        {hasFilters && (
          <Button
            variant="ghost"
            onClick={onReset}
          >
            پاک کردن فیلترها
          </Button>
        )}

        <Button
          variant="primary"
          onClick={onAdd}
        >
          <Icon
            name="plus"
            className="ml-2 h-4 w-4"
          />

          افزودن بازیکن
        </Button>

      </div>

    </div>
  );
}